/* eslint-disable camelcase */

import React, { ReactElement } from 'react'
import styled from 'styled-components'
import { render } from 'react-dom'
import { format } from 'd3-format'
import { DashTheme, DataTrajectoryPoint, LithologyPoint, TooltipData, Uom } from './types'
import theme from 'live/services/theme'
import { channels, channelsColors } from './utils'
import i18n from 'live/services/i18n'
import { BHA } from 'webapp/js/types'

const f = format('.02f')
// TODO update types once follow bha branch is merged
type Props = {
    uom: Uom
    className?: string
    md?: number
    lithology?: LithologyPoint
    bha?: BHA
    trajectory?: DataTrajectoryPoint
    dashTheme: DashTheme
}
function Tooltip({ uom, className, md, lithology, bha, trajectory, dashTheme, ...props }: Props): ReactElement {
    const { component_type, length } = bha ?? {}

    function renderLithology(lithology: LithologyPoint): ReactElement[] | null {
        if (lithology && Array.isArray(lithology.lithologys)) {
            return lithology.lithologys.map(e => {
                return (
                    <div key={lithology.timestamp}>
                        {e.description}&nbsp;({e.lithPc.value}
                        {e.lithPc.uom})
                    </div>
                )
            })
        }

        return null
    }

    const trajectoryItems = ['tvd', 'azi', 'incl', 'vertSect', 'dispEw', 'dispNs']
    const borderColor = dashTheme === 'light' ? 'black' : 'white'

    return (
        <TooltipStyles dashTheme={dashTheme} className={className}>
            <dl aria-label={'trajectory information'}>
                <div className={'dataGroupContainer'}>
                    <dt>md:</dt>
                    <dd>
                        {md}
                        <abbr>{uom?.trajectory?.md}</abbr>
                    </dd>
                </div>
                {trajectoryItems.map(p => (
                    <div className={'dataGroupContainer'} key={p}>
                        <dt>{p}:</dt>
                        <dd>
                            {trajectory ? trajectory[p] : '-'}
                            <abbr>{uom?.trajectory ? uom.trajectory[p] : ''}</abbr>
                        </dd>
                    </div>
                ))}
            </dl>
            <dl aria-label={'aditional information'}>
                {channels.map(p => (
                    <div className={'dataGroupContainer'} key={p}>
                        <div
                            style={{ background: channelsColors[p], borderColor: borderColor }}
                            className={'channelColor'}
                        />
                        <dt>{p}:</dt>
                        <dd>
                            {props[p] ? f(props[p]) : '-'}
                            <abbr>{uom[p]}</abbr>
                        </dd>
                    </div>
                ))}
                <div className={'dataGroupContainer'}>
                    <dt>BHA:</dt>
                    <dd>
                        {component_type ? (
                            <>
                                {component_type} {length}
                                <abbr>{uom?.trajectory?.md}</abbr>
                            </>
                        ) : (
                            '-'
                        )}
                    </dd>
                </div>
                <div className={'dataGroupContainer'}>
                    <dt>{i18n('Lithology')}:</dt>
                    <dd>{renderLithology(lithology) ?? '-'}</dd>
                </div>
            </dl>
        </TooltipStyles>
    )
}

const TooltipStyles = styled.div`
    display: flex;
    width: 100%;
    padding: 10px 10px;
    overflow: hidden;
    height: 100%;
    dl {
        font-family: SourceSansPro-Regular, Arial, Helvetica Neue, Helvetica, sans-serif;
        margin: 0;
        flex: 1;
        .dataGroupContainer {
            display: flex;
            align-items: center;
            position: relative;

            .channelColor {
                width: 13px;
                height: 13px;
                border: 1px solid white;
                /* border-radius: 3px; */
                position: absolute;
                left: -10px;
                top: 50%;
                transform: translate(-80%, -50%);
            }
        }
        dt,
        dd {
            height: 18px;
        }
        dt {
            display: flex;
            align-items: center;
            margin-right: 5px;
            font-weight: bold;
            color: ${({ dashTheme }) => (dashTheme === 'light' ? theme['text-color'] : 'white')};
        }
        dd {
            font-family: SourceSansPro-Regular, Arial, Helvetica Neue, Helvetica, sans-serif;
            font-weight: normal;
            color: ${({ dashTheme }) => (dashTheme !== 'dark' ? theme['text-color'] : 'white')};
            abbr {
                font-size: 10px;
                color: ${({ dashTheme }) => (dashTheme !== 'dark' ? '#999' : 'white')};
                margin-left: 1px;
            }
        }
    }
`

export function renderTooltip(data: TooltipData, el: HTMLElement, uom: Uom, dashTheme: DashTheme): void {
    render(<Tooltip {...data} uom={uom} dashTheme={dashTheme} />, el)
}
